import { useContext, useMemo } from 'react';
import dayjs from 'dayjs';
import { DEFAULT_DATE_FORMAT, SVG_SIZE_L } from 'src/constants';
import {
  useSession,
  useConversations,
  useConversationParams,
  useComparingConversationsParams,
} from 'src/hooks';
import { Conversation } from 'src/types';
import { SpinnerSimple } from 'src/v2/commonComponents/SpinnerSimple';
import styles from './ConversationsList.module.scss';
import { ConversationsGroupedByDate } from './components/ConversationsGroupedByDate';
import { UpsellChatsMessage } from 'src/components/UpsellChatsMessage';
import { EmptyContainer } from './components/EmptyContainer';
import ForwardRefContext from 'src/contexts/ForwardRefContext';

export const ConversationsList = () => {
  const { isOpenTier } = useSession();

  const {
    conversations = [],
    isConversationsLoading,
    isConversationsFetching,
  } = useConversations();

  const { currentConversationId } = useConversationParams();

  const { firstComparedConvId, secondComparedConvId, isCompareMode } =
    useComparingConversationsParams();

  const { scrollableConversationsContainerRef } = useContext(ForwardRefContext);

  const groupedConversations: Record<string, Conversation[]> = useMemo(() => {
    const acc: Record<string, Conversation[]> = {};

    if (conversations.length === 0) {
      return acc;
    }

    for (const conversation of conversations) {
      const groupedDate = dayjs(conversation.updated_at).format(
        DEFAULT_DATE_FORMAT,
      );

      if (!acc[groupedDate]) {
        acc[groupedDate] = [conversation];
      } else {
        acc[groupedDate] = [...acc[groupedDate], conversation];
      }
    }

    return acc;
  }, [conversations]);

  const isEmpty = useMemo(() => {
    return Object.keys(groupedConversations).length === 0;
  }, [groupedConversations]);

  const selectedConversationIds = isCompareMode
    ? [firstComparedConvId, secondComparedConvId]
    : [currentConversationId];

  return (
    <div className={styles.root} ref={scrollableConversationsContainerRef}>
      {isConversationsLoading ? (
        <div className={styles.spinnerWrapper}>
          <SpinnerSimple />
        </div>
      ) : (
        <>
          <div className={styles.container}>
            {Object.entries(groupedConversations).map(([date, group]) => (
              <ConversationsGroupedByDate
                key={date}
                date={date}
                conversations={group}
                selectedConversationIds={selectedConversationIds}
              />
            ))}
          </div>

          {isOpenTier && <UpsellChatsMessage />}

          {!isEmpty && isConversationsFetching && (
            <SpinnerSimple size={SVG_SIZE_L} />
          )}

          {!isConversationsLoading && isEmpty && <EmptyContainer />}
        </>
      )}
    </div>
  );
};
